import { Typography } from "@mui/material";
import React from "react";
import styled, { css, ThemeProvider, keyframes } from "styled-components";
import theme from "../../../style/theme";
import DeleteFillIcon from "../../icons/DeleteFillIcon/delete-fill-2.svg";
import LoadingLineIcon from "../../icons/LoadingLineIcon";
import TimeLineIcon from "../../icons/TimeLineIcon";
import { CoordinatePinProps } from "./coordinate-pin-props";

const LOADING_TEXT = "불러오는 중";

export const CoordinatePin: React.FC<CoordinatePinProps> = ({
  memberName,
  id,
  positionStyle,
  title,
  leftBody,
  leftBodyColor,
  rightBody,
  footer,
  ...rest
}) => {
  return (
    <React.Fragment>
      <ThemeProvider theme={theme}>
        <CoordinatePinContainer style={positionStyle}>
          <CoordinatePinContent>
            <CoordinatePinItem {...rest}>{memberName}</CoordinatePinItem>
            {title ? (
              <TextFieldContainer className="hover-container">
                <TextFieldTitle>{title}</TextFieldTitle>
                <TextFieldBody>
                  <LeftBody color={leftBodyColor}>{leftBody}</LeftBody>
                  <RightBody>{rightBody}</RightBody>
                </TextFieldBody>
                <FooterContainer>
                  <LeftFooter>
                    <TimeLineIcon width="14px" height="14px" fill="#979da8" />
                  </LeftFooter>
                  <RightFooter>{footer} 요청</RightFooter>
                </FooterContainer>
              </TextFieldContainer>
            ) : (
              <LoadingContainer className="hover-container">
                <LoadingImageContent>
                  <LoadingLineIcon />
                </LoadingImageContent>
                <LoadingTextContent>
                  <LoadingText>{LOADING_TEXT}</LoadingText>
                </LoadingTextContent>
              </LoadingContainer>
            )}
          </CoordinatePinContent>
        </CoordinatePinContainer>
      </ThemeProvider>
    </React.Fragment>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -81px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -96px) scale(1);
  }
`;

const CoordinatePinContainer = styled.div`
  position: relative;
`;

const CoordinatePinContent = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, calc(-100% - 12px));
  &:hover {
    z-index: 99999;
    & > .hover-container {
      display: flex;
    }
  }
`;

const CoordinatePinItem = styled(Typography).attrs({
  variant: "overline",
}) <any>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  min-width: 33px;
  height: 33px;
  font-size: 16px;
  font-weight: 600;
  box-sizing: border-box;
  padding: 8px 26px;
  border-radius: 8px;
  border: 1px solid ${theme.palette.primitives.gray200};
  color: ${(props) => props.color};
  transform: scale(0.9);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.18);
  background: ${(props) => props.backgroundColor};
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  ${(props) => props.sx};

  &::before {
    position: absolute;
    display: inline-block;
    content: "";
    box-sizing: border-box;
    transition: all 0.3s ease;
    ${(props) => {
    switch (props.type) {
      case "left":
        return css`
            width: 8px;
            height: 16px;
            transform: matrix(-1, 0, 0, 1, 0, 0);
            bottom: calc(50% - 16px / 2);
            right: calc(100%);
            border-left: 8px solid ${(props: any) => props.backgroundColor};
          `;
      case "right":
        return css`
            color: blue;
            width: 8px;
            height: 16px;
            transform: matrix(-1, 0, 0, 1, 0, 0);
            bottom: calc(50% - 16px / 2);
            left: calc(100%);
            border-right: 8px solid ${(props: any) => props.backgroundColor};
          `;
      case "top":
        return css`
            width: 16px;
            height: 8px;
            transform: matrix(-1, 0, 0, 1, 0, 0);
            bottom: calc(100%);
            left: ${(props: any) => {
            if (props.detailType === "left") return "calc(16px)";
            if (props.detailType === "right") return "calc(100% - 16px * 2);";
            if (props.detailType === "center") return "calc(50% - 16px / 2);";
          }};

            border-bottom: 8px solid ${(props: any) => props.backgroundColor};
          `;
      case "bottom":
        return css`
            width: 8px;
            height: 12px;
            top: calc(100%);
            left: ${(props: any) => {
            if (props.detailType === "left") return "calc(8px)";
            if (props.detailType === "right") return "calc(100% - 8px * 2);";
            if (props.detailType === "center") return "calc(50% - 8px / 2);";
          }};
            background: ${(props: any) => props.backgroundColor};
            border-left: 1px solid ${theme.palette.primitives.gray200};
            border-right: 1px solid ${theme.palette.primitives.gray200};
            border-bottom: 1px solid ${theme.palette.primitives.gray200};
            border-bottom-left-radius: 999px;
            border-bottom-right-radius: 999px;
            box-shadow: 0 3px 4px rgba(0, 0, 0, 0.1);
          `;
      default:
        return css`
            width: 16px;
            height: 8px;
            transform: matrix(-1, 0, 0, 1, 0, 0);
            top: calc(100%);
            left: calc(50% - 16px / 2);
            border-top: 8px solid ${(props: any) => props.backgroundColor};
          `;
    }
  }}
  }

  &:hover {
    background: ${(props) => props.hoverBackgroundColor};
    color: ${(props) => props.hoverColor};
    transform: scale(0.95);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.1);

    &::after {
      background: url(${DeleteFillIcon}) no-repeat;
      position: absolute;
      display: inline-block;
      content: "";
      box-sizing: border-box;
      width: 18px;
      height: 18px;
      bottom: calc(50% - 18px / 2);
      right: 4px;
      visibility: ${(props) =>
    props.hoverBackgroundColor === "#deb200" ? "visible" : "hidden"};
    }

    &::before {
      ${(props) => props.type === "bottom" && css`
        height: 14px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.12);
        background: ${props.hoverBackgroundColor};
      `}
    }
  }
`;

const TextFieldContainer = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -96px);
  display: none;
  flex-direction: column;
  background: white;
  padding: 10px 14px;
  width: max-content;
  border: 1px solid #d0d4dc;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  animation: ${fadeIn} 0.3s ease forwards;
  z-index: 1000;
`;

const TextFieldTitle = styled.div`
  font-size: 12px;
  color: #4d535c;
  font-weight: 700;
  line-height: 14px;
  display: flex;
  align-items: center;
  letter-spacing: 0.5px;
  margin: 0 0 4px 0;
`;

const TextFieldBody = styled.div`
  display: flex;
  font-size: 12px;
  color: #4d535c;
`;

interface LeftBodyProps {
  color: string;
}

const LeftBody = styled.div<LeftBodyProps>`
  font-weight: 700;
  font-size: 12px;
  line-height: 14px;
  display: flex;
  align-items: center;
  letter-spacing: 0.5px;
  margin: 2px 4px 2px 0;
  color: ${(props) => props.color};
`;

const RightBody = styled.div`
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  display: flex;
  align-items: center;
  letter-spacing: 0.5px;
  color: #4d535c;
`;

const FooterContainer = styled.div`
  display: flex;
  margin: 4px 0 0 0;
  align-items: center;
`;

const LeftFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const RightFooter = styled.div`
  margin-left: 4px;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  display: flex;
  align-items: center;
  color: #979da8;
`;

const LoadingContainer = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -91px);
  display: none;
  flex-direction: column;
  justify-content: center;
  background: white;
  width: 131px;
  height: 62px;
  border: 1px solid #d0d4dc;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  animation: ${fadeIn} 0.3s ease forwards;
`;

const LoadingImageContent = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;
const LoadingTextContent = styled.div`
  display: flex;
  justify-content: center;
`;
const LoadingText = styled.div`
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  display: flex;
  align-items: center;
  letter-spacing: 0.5px;
  color: #979da8;
`;

CoordinatePin.defaultProps = {
  type: "bottom",
  detailType: "center",
};
